import { InfoCircle } from "@untitledui/icons";
import { useState } from "react";
import type {
  OpenCollaborationInsightsPerformance,
  TargetCollaborationInsightsPerformance,
} from "@/api/collaborations";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartTooltip } from "./chart-tooltip";
import { FindMoreCreatorsCard } from "./find-more-creators-card";

type Props = {
  collaborationId: string;
  data: TargetCollaborationInsightsPerformance | OpenCollaborationInsightsPerformance | null | undefined;
  isLoading: boolean;
  mode: "invited" | "accepted";
  onModeChange: (mode: "invited" | "accepted") => void;
  showRecommendations?: boolean;
  /** When false, hides the Invited/Accepted toggle and shows only the active mode label. */
  showModeToggle?: boolean;
  /** Currently-selected segment key (controlled by the panel). */
  selectedKey?: string | null;
  /** Called when a segment or its legend item is clicked. Pass `null` to clear. */
  onSelect?: (key: string | null) => void;
};

type SegmentDef = {
  key: string;
  label: string;
  background: string;
  count: number;
  detail?: string;
  tooltip: string;
};

const GMV_GRADIENT = "linear-gradient(90deg, #FEB154 0%, #9DEAFF 100%)";
const VIEWS_GRADIENT = "linear-gradient(90deg, #D5A9FF 0%, #FFBF8B 100%)";
const NOT_POSTED_COLOR = "#A4A7AE";

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function pluralize(count: number, singular: string, plural: string) {
  return count === 1 ? singular : plural;
}

export function InsightsPerformanceTab({
  collaborationId,
  data,
  isLoading,
  mode,
  onModeChange,
  showRecommendations,
  showModeToggle = true,
  selectedKey = null,
  onSelect,
}: Props) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 py-2">
        <div className="flex gap-6 items-start">
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-5 w-64 bg-white/20" />
            <Skeleton className="h-4 w-full max-w-md bg-white/10" />
            <Skeleton className="h-4 w-3/4 max-w-md bg-white/10" />
          </div>
          {showRecommendations && <Skeleton className="h-12 w-96 bg-white/10 rounded-xl" />}
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-40 bg-white/20" />
            <Skeleton className="h-8 w-56 bg-white/10 rounded-full" />
          </div>
          <Skeleton className="h-8 w-full rounded bg-white/10" />
          <div className="flex gap-8">
            {(["seg-a", "seg-b", "seg-c"] as const).map((key) => (
              <Skeleton key={key} className="h-10 w-40 bg-white/10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-sm text-white/50 text-center py-6">No performance data available</div>;
  }

  const { totalCreators, segments } = data;
  const currency = segments.generatingGmv.currency ?? "GBP";
  const total = Math.max(1, totalCreators);

  const isPredicted = mode === "invited";

  const segmentDefs: SegmentDef[] = [
    {
      key: "generating_gmv",
      label: isPredicted ? "Predicted to Generate GMV" : "Is Generating GMV",
      background: GMV_GRADIENT,
      count: segments.generatingGmv.count,
      detail: `${formatCurrency(segments.generatingGmv.gmv, currency)} from ${segments.generatingGmv.count.toLocaleString()} ${pluralize(segments.generatingGmv.count, "Creator", "Creators")}`,
      tooltip: `${segments.generatingGmv.count.toLocaleString()} ${pluralize(segments.generatingGmv.count, "creator", "creators")} ${isPredicted ? "predicted to generate" : "generating"} GMV.`,
    },
    {
      key: "generating_views",
      label: isPredicted ? "Predicted to Generate Views" : "Is Generating Views",
      background: VIEWS_GRADIENT,
      count: segments.generatingViews.count,
      detail: `${segments.generatingViews.views.toLocaleString()} views from ${segments.generatingViews.count.toLocaleString()} ${pluralize(segments.generatingViews.count, "Creator", "Creators")}`,
      tooltip: `${segments.generatingViews.count.toLocaleString()} ${pluralize(segments.generatingViews.count, "creator", "creators")} ${isPredicted ? "predicted to generate" : "generating"} views only.`,
    },
    {
      key: "not_posted",
      label: isPredicted ? "Not likely to post" : "Has not posted",
      background: NOT_POSTED_COLOR,
      count: segments.notPosted.count,
      detail: `${segments.notPosted.count.toLocaleString()} ${pluralize(segments.notPosted.count, "Creator", "Creators")}`,
      tooltip: isPredicted
        ? `${segments.notPosted.count.toLocaleString()} ${pluralize(segments.notPosted.count, "creator", "creators")} not likely to post.`
        : `${segments.notPosted.count.toLocaleString()} ${pluralize(segments.notPosted.count, "creator", "creators")} have not posted yet.`,
    },
  ];

  const hasHover = hoveredKey !== null;
  const hasSelection = selectedKey !== null;
  const interactive = !!onSelect;

  const dimFor = (key: string) => {
    const isHovered = hoveredKey === key;
    const isSelected = selectedKey === key;
    return hasHover ? !isHovered && !isSelected : hasSelection ? !isSelected : false;
  };

  const handleSelect = (key: string) => {
    if (!onSelect) return;
    onSelect(selectedKey === key ? null : key);
  };

  return (
    <div className="flex flex-col gap-6 py-2">
      {/* Header: stacked below ~1100 px, side-by-side above */}
      <div className="flex flex-col gap-3 panels:flex-row panels:items-start panels:gap-6">
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <p className="text-base font-medium text-white">
            Here's a breakdown of everyone who has accepted your invite request
          </p>
          <p className="text-sm text-white/60">
            {isPredicted
              ? `Here's the predicted performance of this collaboration, based on the current behaviour of the ${totalCreators.toLocaleString()} ${pluralize(totalCreators, "creator", "creators")} you've invited.`
              : `Here's how this collab is performing, based on the ${totalCreators.toLocaleString()} ${pluralize(totalCreators, "creator", "creators")} who have accepted your invite.`}
          </p>
        </div>
        {showRecommendations && <FindMoreCreatorsCard collaborationId={collaborationId} layout="inline" />}
      </div>

      {/* Predicted / Generated Output section */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-semibold text-white">
              {isPredicted ? "Predicted Output" : "Generated Output"}
            </span>
            <InfoCircle className="w-4 h-4 text-white/60" />
          </div>
          {/* Mode toggle pill — omitted entirely when only one mode is meaningful (e.g. open collabs) */}
          {showModeToggle && (
            <div className="flex items-center p-1 rounded-full" style={{ background: "rgba(0, 0, 0, 0.20)" }}>
              {(["invited", "accepted"] as const).map((m) => {
                const isActive = mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => onModeChange(m)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                      isActive ? "bg-white text-[#6941C6]" : "text-white hover:text-white/80"
                    }`}
                  >
                    {m === "invited" ? "Invited" : "Accepted"}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Stacked progress bar */}
        <div className="flex w-full gap-1 h-[33px]">
          {segmentDefs.map((seg) => {
            const pct = (seg.count / total) * 100;
            if (pct === 0) return null;
            const isHovered = hoveredKey === seg.key;
            const isSelected = selectedKey === seg.key;
            const active = isHovered || isSelected;
            const dimmed = dimFor(seg.key);

            const BarTag = interactive ? "button" : "div";
            return (
              <BarTag
                key={seg.key}
                type={interactive ? "button" : undefined}
                className={`h-full relative transition-opacity duration-200 ${
                  dimmed ? "opacity-35" : "opacity-100"
                } ${interactive ? "cursor-pointer outline-none" : ""}`}
                style={{
                  width: `${pct}%`,
                  background: seg.background,
                  borderRadius: 4,
                  boxShadow: isSelected
                    ? "0 2px 6px rgba(0,0,0,0.18)"
                    : isHovered
                      ? "0 2px 6px rgba(0,0,0,0.14)"
                      : "none",
                  transform: active ? "scaleY(1.06)" : "scaleY(1)",
                  filter: isSelected ? "brightness(1.06)" : "brightness(1)",
                  transformOrigin: "center",
                  transition: "box-shadow 200ms ease, transform 200ms ease, filter 200ms ease",
                }}
                onMouseEnter={() => setHoveredKey(seg.key)}
                onMouseLeave={() => setHoveredKey(null)}
                onClick={interactive ? () => handleSelect(seg.key) : undefined}
                aria-pressed={interactive ? isSelected : undefined}
              >
                {isHovered && <ChartTooltip>{seg.tooltip}</ChartTooltip>}
              </BarTag>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-8 flex-wrap">
            {segmentDefs.map((seg) => {
              const isHovered = hoveredKey === seg.key;
              const isSelected = selectedKey === seg.key;
              const active = isHovered || isSelected;
              const dimmed = dimFor(seg.key);
              const LegendTag = interactive ? "button" : "div";
              return (
                <LegendTag
                  key={seg.key}
                  type={interactive ? "button" : undefined}
                  className={`flex flex-col gap-1 transition-opacity duration-200 text-left ${
                    dimmed ? "opacity-35" : "opacity-100"
                  } ${interactive ? "cursor-pointer outline-none" : ""}`}
                  onMouseEnter={() => setHoveredKey(seg.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                  onClick={interactive ? () => handleSelect(seg.key) : undefined}
                  aria-pressed={interactive ? isSelected : undefined}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: seg.background }} />
                    <span
                      className={`text-sm text-white transition-[font-weight] ${
                        active ? "font-bold" : "font-semibold"
                      }`}
                    >
                      {seg.label}
                    </span>
                  </div>
                  {seg.detail && <span className="text-sm text-white/60 ml-4">{seg.detail}</span>}
                </LegendTag>
              );
            })}
          </div>
          <div className="px-3 py-1.5 rounded-md bg-white/[0.08] border border-white/10 text-sm font-medium text-white">
            {isPredicted
              ? `${totalCreators.toLocaleString()} Pending Invite`
              : `Of ${totalCreators.toLocaleString()} ${showModeToggle ? "Accepted" : "creators"}`}
          </div>
        </div>
      </div>
    </div>
  );
}
